import {inject, Injectable, isDevMode} from '@angular/core';
import {RuntimeService} from "../../../core/services/runtime.service";
import {WindowRefService} from "../window-ref/window-ref.service";
import {Cart} from "../../../core/models/cart.model";
import {Product} from "../../../core/models/product.model";
import {Course} from "../../../core/models/course.model";
import {Workshop} from "../../../core/models/workshop.model";
import {environment} from "../../../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AnalyticsGaService {
    private runtimeService = inject(RuntimeService);
    private windowRef = inject(WindowRefService);

    addToCart(item: Product | Course | Workshop, type: 'Product' | 'Course' | 'Event', quantity: number = 1, listId?: string, listName?: string) {
        const itemData = {
            item_id: item.uuid || '',
            item_name: type === 'Product' ? (item as Product).name || '' : (item as Course).title || '',
            index: 0,
            item_brand: "Rooftop",
            item_category: type === 'Product' ? (item as Product).categories?.[0]?.name || 'uncategorized' : type === 'Course' ? 'Course' : 'Workshop',
            price: type === 'Product' ? (item as Product).price || 0 : (item as Course).cost || 0,
            quantity,
            ...(listId && {item_list_id: listId}),
            ...(listName && {item_list_name: listName})
        };

        this.executeGtagEvent("add_to_cart", {
            currency: "INR",
            value: itemData.price * quantity,
            items: [itemData]
        });
    }

    selectItem(item: Product | Course | Workshop, type: 'Product' | 'Course' | 'Event', listId: string, listName: string, index: number = 0) {
        const itemData = {
            item_id: item.uuid || '',
            item_name: type === 'Product' ? (item as Product).name || '' : (item as Course).title || '',
            index,
            item_brand: "Rooftop",
            item_category: type === 'Product' ? (item as Product).categories?.[0]?.name || 'uncategorized' : type === 'Course' ? 'Course' : 'Workshop',
            price: type === 'Product' ? (item as Product).price || 0 : (item as Course).cost || 0,
            quantity: 1
        };

        this.executeGtagEvent("select_item", {
            item_list_id: listId,
            item_list_name: listName,
            items: [itemData]
        });
    }

    removeFromCart(item: Product | Course | Workshop, type: 'Product' | 'Course' | 'Event', quantity: number = 1) {
        const itemData = {
            item_id: item.uuid || '',
            item_name: type === 'Product' ? (item as Product).name || '' : (item as Course).title || '',
            index: 0,
            item_brand: "Rooftop",
            item_category: type === 'Product' ? (item as Product).categories?.[0]?.name || 'uncategorized' : type === 'Course' ? 'Course' : 'Workshop',
            price: type === 'Product' ? (item as Product).price || 0 : (item as Course).cost || 0,
            quantity
        };

        this.executeGtagEvent("remove_from_cart", {
            currency: "INR",
            value: itemData.price * quantity,
            items: [itemData]
        });
    }

    viewItem(item: Product | Course | Workshop, type: 'Product' | 'Course' | 'Event') {
        const itemData = {
            item_id: item.uuid || '',
            item_name: type === 'Product' ? (item as Product).name || '' : (item as Course).title || '',
            index: 0,
            item_brand: "Rooftop",
            item_category: type === 'Product' ? (item as Product).categories?.[0]?.name || 'uncategorized' : type === 'Course' ? 'Course' : 'Workshop',
            price: type === 'Product' ? (item as Product).price || 0 : (item as Course).cost || 0,
            quantity: 1,
            item_list_id: type === 'Product' ? "rooftop_products" : type === 'Course' ? "rooftop_courses" : "rooftop_workshops",
            item_list_name: type === 'Product' ? "Rooftop Products" : type === 'Course' ? "Rooftop Courses" : "Rooftop Workshops"
        };

        this.executeGtagEvent("view_item", {
            currency: "INR",
            value: itemData.price,
            items: [itemData]
        });
    }

    viewItemList(items: (Product | Course | Workshop)[], type: 'Product' | 'Course' | 'Event', listId: string, listName: string) {
        const gtagItems = items.map((item, index) => ({
            item_id: item.uuid || '',
            item_name: type === 'Product' ? (item as Product).name || '' : (item as Course).title || '',
            index,
            item_brand: "Rooftop",
            item_category: type === 'Product' ? (item as Product).categories?.[0]?.name || 'uncategorized' : type === 'Course' ? 'Course' : 'Workshop',
            price: type === 'Product' ? (item as Product).price || 0 : (item as Course).cost || 0,
            quantity: 1
        }));
        this.executeGtagEvent("view_item_list", {
            item_list_id: listId,
            item_list_name: listName,
            items: gtagItems
        });
    }

    addPromoCode(cart: Cart, promoCode: string) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("add_promo_code", {
            coupon: promoCode,
            items,
            value: cart.total || 0,
            currency: "INR"
        });
    }

    viewCart(cart: Cart) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("view_cart", {
            currency: "INR",
            value: cart.total || 0,
            items
        });
    }

    beginCheckout(cart: Cart) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("begin_checkout", {
            currency: "INR",
            value: cart.total || 0,
            items
        });
    }

    addShippingInfo(cart: Cart, shippingDetails: { shipping_tier: string; address_uuid: string }) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("add_shipping_info", {
            currency: "INR",
            value: cart.total || 0,
            shipping_tier: shippingDetails.shipping_tier,
            address_uuid: shippingDetails.address_uuid,
            items
        });
    }

    addPaymentInfo(paymentType: string, cart: Cart) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("add_payment_info", {
            currency: "INR",
            value: cart.total || 0,
            payment_type: paymentType,
            items
        });
    }

    purchase(cart: Cart, transactionId: string) {
        const items = cart.cartItems?.map((item, index) => this.mapCartItemToGaItem(item, index)).filter(item => item !== null) || [];
        this.executeGtagEvent("purchase", {
            transaction_id: transactionId,
            shipping: cart.delivery_details?.[0]?.rate || 0,
            currency: "INR",
            value: cart.total || 0,
            items
        });
    }

    trackPageView(url: string) {
        if (this.runtimeService.isBrowser) {
            const gtag = this.gtag();
            if (gtag) {
                gtag('config', environment.firebaseConfig.measurementId, {
                    'page_path': url,
                    'debug_mode': isDevMode()
                });
            }
        }
    }

    private gtag() {
        return this.windowRef.nativeWindow?.['gtag'];
    }

    private executeGtagEvent(eventName: string, params: any) {
        if (this.runtimeService.isBrowser) {
            const gtag = this.gtag();
            if (gtag) {
                gtag("event", eventName, params);
            }
        }
    }

    private mapCartItemToGaItem(item: any, index: number): any {
        if (item.entity_type === 'Product') {
            const product = item.entity as Product;
            return {
                item_id: product.uuid || '',
                item_name: product.name || '',
                index,
                item_brand: "Rooftop",
                item_category: product.categories?.[0]?.name || 'uncategorized',
                price: item.variant ? item.variant.price : product.price || 0,
                quantity: item.quantity || 1
            };
        } else if (item.entity_type === 'Course') {
            const course = item.entity as Course;
            return {
                item_id: course.uuid || '',
                item_name: course.title || '',
                index,
                item_brand: "Rooftop",
                item_category: 'Course',
                price: course.cost || 0,
                quantity: item.quantity || 1
            };
        } else if (item.entity_type === 'Event') {
            const workshop = item.entity as Workshop;
            return {
                item_id: workshop.uuid || '',
                item_name: workshop.title || '',
                index,
                item_brand: "Rooftop",
                item_category: 'Workshop',
                price: workshop.cost || 0,
                quantity: item.quantity || 1
            };
        }
        return null;
    }
}
